import { GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import React from 'react'

/**
 * Public, login-free page describing how to delete a Goodfellas account and
 * what data is removed vs. retained.
 *
 * Required by the Google Play Data Safety form (a public account/data deletion
 * URL) and complements the in-app deletion flow (App Store / Google Play
 * requirement). See issue #864.
 */
export default function AccountDeletionPage() {
  return (
    <>
      <Head>
        <title>Exclusão de conta — Goodfellas</title>
        <meta
          name="description"
          content="Como excluir sua conta Goodfellas e o que acontece com os seus dados."
        />
        <meta name="robots" content="index,follow" />
      </Head>
      <main className="bg-white text-gray-800">
        <div className="max-w-3xl mx-auto px-5 py-12">
          <h1 className="text-3xl font-bold text-gray-900">
            Exclusão de conta
          </h1>
          <p className="mt-4 text-gray-600">
            Você pode excluir sua conta Goodfellas a qualquer momento, direto
            pelo aplicativo ou pelo site. A exclusão desativa o seu perfil e o
            remove da plataforma.
          </p>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-gray-900">
              Pelo aplicativo (iOS e Android)
            </h2>
            <ol className="mt-4 list-decimal pl-6 space-y-2 text-gray-700">
              <li>Abra o app Goodfellas e faça login.</li>
              <li>
                Toque no ícone de engrenagem (canto superior direito) e escolha{' '}
                <strong>Editar perfil</strong>.
              </li>
              <li>
                Role até o final, na seção <strong>Zona de perigo</strong>.
              </li>
              <li>
                Toque em <strong>Excluir minha conta</strong>, confirme e pronto.
              </li>
            </ol>
          </section>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-gray-900">Pelo site</h2>
            <ol className="mt-4 list-decimal pl-6 space-y-2 text-gray-700">
              <li>
                Acesse{' '}
                <a
                  href="https://goodfellas.app/avaliacao"
                  className="text-rose-600 underline"
                >
                  goodfellas.app
                </a>{' '}
                e faça login.
              </li>
              <li>
                Abra <strong>Editar perfil</strong> e, na seção{' '}
                <strong>Excluir conta</strong>, confirme a exclusão.
              </li>
            </ol>
          </section>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-gray-900">
              Não consegue acessar sua conta?
            </h2>
            <p className="mt-4 text-gray-700">
              Se você não consegue mais entrar no app ou no site, solicite a
              exclusão pela nossa{' '}
              <a href="/contato" className="text-rose-600 underline">
                página de contato
              </a>
              , informando o número de telefone cadastrado. Confirmaremos sua
              identidade e processaremos a exclusão.
            </p>
          </section>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-gray-900">
              O que é excluído e o que é mantido
            </h2>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-gray-700">
              <li>
                <strong>Excluído / desativado:</strong> seu perfil é desativado
                e deixa de aparecer no Goodfellas — você some das buscas, do
                ranking, dos cards e deixa de ser localizável por outros
                atletas. Você não consegue mais entrar com essa conta.
              </li>
              <li>
                <strong>Mantido por obrigação legal/fiscal:</strong> registros
                financeiros (pagamentos/cobranças) são retidos pelo período
                exigido pela legislação, pois não podem ser apagados
                imediatamente.
              </li>
              <li>
                <strong>Referências históricas:</strong> dados já compartilhados
                com terceiros (ex.: confrontos/treinos dos quais você participou)
                podem permanecer associados ao registro do evento, sem expor o
                seu perfil.
              </li>
            </ul>
            <p className="mt-4 text-sm text-gray-500">
              A exclusão é permanente e não pode ser desfeita. Para criar uma
              nova conta depois, será necessário um novo cadastro.
            </p>
          </section>
        </div>
      </main>
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale || 'pt', ['common'])),
    },
  }
}
